import React, { useEffect, useRef, useState } from 'react';
import { auth } from '../../firebase/firebaseConfig';
import {
  listUserCookbooksForPublish,
  getDeckCardCount,
  publishCookbook,
} from '../../services/communityCookbookService';
import './PublishCookbookModal.css';

const PublishCookbookModal = ({ userId, open, onClose, onPublished }) => {
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeckId, setSelectedDeckId] = useState('');
  const [cardCounts, setCardCounts] = useState({});
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [language, setLanguage] = useState('');
  const [tags, setTags] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [cookbookMenuOpen, setCookbookMenuOpen] = useState(false);
  const cookbookMenuRef = useRef(null);

  useEffect(() => {
    if (!open) setCookbookMenuOpen(false);
  }, [open]);

  useEffect(() => {
    if (!cookbookMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (cookbookMenuRef.current && !cookbookMenuRef.current.contains(event.target)) {
        setCookbookMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [cookbookMenuOpen]);

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const loaded = await listUserCookbooksForPublish(userId);
        if (cancelled) return;
        setDecks(loaded);
        const counts = {};
        await Promise.all(
          loaded.map(async (deck) => {
            counts[deck.id] = await getDeckCardCount(userId, deck.id);
          })
        );
        if (!cancelled) setCardCounts(counts);
      } catch (err) {
        if (!cancelled) setError('Failed to load your cookbooks.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [open, userId]);

  useEffect(() => {
    if (!selectedDeckId) return;
    const deck = decks.find((d) => d.id === selectedDeckId);
    if (!deck) return;
    setName(deck.name || '');
    setDescription(deck.description || '');
    setLanguage(deck.language || '');
    setTags((deck.tags || []).join(', '));
  }, [selectedDeckId, decks]);

  const selectedDeck = decks.find((d) => d.id === selectedDeckId);
  const selectedCardCount = selectedDeckId ? cardCounts[selectedDeckId] || 0 : 0;
  const canPublish = selectedDeckId && selectedCardCount > 0 && name.trim();
  const cookbookTriggerLabel = selectedDeck
    ? `${selectedDeck.name} (${cardCounts[selectedDeck.id] ?? '…'} cards)`
    : 'Select a cookbook…';

  const handlePublish = async () => {
    if (!canPublish || publishing) return;
    setPublishing(true);
    setError('');
    try {
      const user = auth.currentUser;
      const result = await publishCookbook(
        userId,
        selectedDeckId,
        {
          name: name.trim(),
          description: description.trim(),
          language: language.trim() || 'Other',
          tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        },
        {
          displayName: user?.displayName || '',
          photoURL: user?.photoURL || '',
        }
      );
      onPublished?.(result);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to publish cookbook.');
    } finally {
      setPublishing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="publish-cookbook-modal__overlay" onClick={onClose}>
      <div
        className="publish-cookbook-modal__panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-cookbook-modal-title"
      >
        <button type="button" className="publish-cookbook-modal__close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <h2 id="publish-cookbook-modal-title" className="publish-cookbook-modal__title">
          Publish to Community
        </h2>
        <p className="publish-cookbook-modal__subtitle">
          Share one of your cookbooks with other learners.
        </p>

        {loading ? (
          <p className="publish-cookbook-modal__loading">Loading your cookbooks…</p>
        ) : decks.length === 0 ? (
          <p className="publish-cookbook-modal__empty">
            You don&apos;t have any library cookbooks to publish. Create one in your library first.
          </p>
        ) : (
          <>
            <label className="publish-cookbook-modal__label">
              Choose cookbook
              <div className="publish-cookbook-modal__select-wrap" ref={cookbookMenuRef}>
                <button
                  type="button"
                  className="publish-cookbook-modal__select-trigger"
                  onClick={() => setCookbookMenuOpen((open) => !open)}
                  aria-expanded={cookbookMenuOpen}
                  aria-haspopup="listbox"
                >
                  <span className="publish-cookbook-modal__select-trigger-label">
                    {cookbookTriggerLabel}
                  </span>
                  <i className="ti ti-chevron-down" aria-hidden="true" />
                </button>
                {cookbookMenuOpen && (
                  <div className="publish-cookbook-modal__select-menu" role="listbox">
                    {decks.map((deck) => {
                      const selected = selectedDeckId === deck.id;
                      return (
                        <button
                          key={deck.id}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          className={`publish-cookbook-modal__select-option${selected ? ' publish-cookbook-modal__select-option--selected' : ''}`}
                          onClick={() => {
                            setSelectedDeckId(deck.id);
                            setCookbookMenuOpen(false);
                          }}
                        >
                          {deck.name} ({cardCounts[deck.id] ?? '…'} cards)
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </label>

            {selectedDeckId && selectedCardCount === 0 && (
              <p className="publish-cookbook-modal__warning">
                Add at least one card with a solution before publishing.
              </p>
            )}

            <label className="publish-cookbook-modal__label">
              Name
              <input
                className="publish-cookbook-modal__input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>

            <label className="publish-cookbook-modal__label">
              Description
              <textarea
                className="publish-cookbook-modal__textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </label>

            <label className="publish-cookbook-modal__label">
              Language
              <input
                className="publish-cookbook-modal__input"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              />
            </label>

            <label className="publish-cookbook-modal__label">
              Tags (comma-separated)
              <input
                className="publish-cookbook-modal__input"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />
            </label>
          </>
        )}

        {error && <p className="publish-cookbook-modal__error">{error}</p>}

        <div className="publish-cookbook-modal__actions">
          <button type="button" className="publish-cookbook-modal__cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="publish-cookbook-modal__publish"
            onClick={handlePublish}
            disabled={!canPublish || publishing || loading}
          >
            {publishing ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishCookbookModal;
